import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef } from 'react';
import { Icon, useLocalizeMessage } from 'libs.nucleus';
import { MultipleChoiceIcon, SingleChoiceIcon } from './consent_choice_type_icons';
import { clampOverlayGroupDelta, nextOverlayPage, overlayPercent, readOverlayKey } from './overlay_keyboard';
import { CONSENT_ELEMENT_TYPES, choiceQuestionLabel } from '../../constants/consent_element_types.constants';
const HEADER_HEIGHT_PX = 22;
// Exact values from Figma's "Fields" component set (node 399:7783, file
// bT7upy29A7Oiu4pCHWC5i8) via get_design_context / get_variable_defs — the "Label" pill
// uses `--color-active-100/200/900`, which are pixel-identical to nucleus's blue-100
// (#dbeafe) / blue-200 (#bfdbfe) / blue-900 (#1e3a8a). Same pair for Checkbox and Single
// select alike — this is not a per-type color.
const HEADER_FILL = '#DBEAFE';
const HEADER_BORDER = '#BFDBFE';
const HEADER_TEXT = '#1E3A8A';
const HEADER_SELECTED_OUTLINE = '#2563EB'; // nucleus blue-600 — matches the option markers' border
/**
 * Floating icon + name label above a SINGLE_SELECT / MULTI_SELECT question's options —
 * matching the icon-and-field-name treatment `ConsentElementMarker` already gives
 * SIGNATURE/INITIALS fields (Figma "Fields" component set, node 399:7783). Dragging the
 * header moves this page's options together, mirroring how a simple element's single
 * marker drags as one piece. The header is rendered and anchored per page, so the drag is
 * scoped to the same set the header is built from.
 */
export const ConsentChoiceGroupHeader = ({ element, pageOptions, pageNumber, isSelected, isTabStop = true, totalPages = 1, onSelect, onUpdate, onDelete, onAnnounce = () => undefined, onPageChange = () => undefined, }) => {
    const translate = useLocalizeMessage();
    const containerRef = useRef(null);
    const dragStartRef = useRef(null);
    const def = CONSENT_ELEMENT_TYPES[element.type];
    const TypeIcon = element.type === 'SINGLE_SELECT' ? SingleChoiceIcon : MultipleChoiceIcon;
    const questionText = choiceQuestionLabel(element.config);
    const accessibleName = questionText
        ? translate('{question}, {label} group', { question: questionText, label: def.label })
        : translate('{label} group', { label: def.label });
    // Anchor above the first option only (declaration/key order, from `pageOptions[0]`) —
    // NOT whichever option currently happens to sit topmost. Options can be dragged
    // independently of the group (SSP-4085), so picking "topmost right now" made the
    // header jump to hover over a different option whenever any option (not just the
    // first) was repositioned. Anchoring to a fixed option keeps the header stable.
    const anchor = pageOptions[0] ?? null;
    if (!anchor) {
        return null;
    }
    const handleMouseDown = (e) => {
        if (e.target.closest('[data-action]')) {
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        onSelect(element.id);
        const parent = containerRef.current?.parentElement?.parentElement;
        if (!parent) {
            return;
        }
        const parentRect = parent.getBoundingClientRect();
        // The whole element's options are snapshotted so the write preserves the full list, but only the
        // ones on the page being dragged from actually move. Dragging the page-2 header used to apply
        // the same dx/dy to the page-1 options too, sliding them off the printed text they were aligned
        // to, on a page the author cannot see while dragging and with nothing downstream to catch it.
        // Options spanning pages was unreachable before SSP-4169 clause 9 — `page` was only written at
        // creation — so on any single-page group, which is every template authored until now, scoping
        // changes nothing.
        const draggedIds = new Set(pageOptions.map((option) => option.id));
        dragStartRef.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            options: (element.config.ec__options ?? []).map((option) => ({ ...option, position: { ...option.position } })),
            draggedIds,
        };
        const onMouseMove = (moveEvent) => {
            const start = dragStartRef.current;
            if (!start) {
                return;
            }
            const dx = (moveEvent.clientX - start.mouseX) / parentRect.width;
            const dy = (moveEvent.clientY - start.mouseY) / parentRect.height;
            const dragged = start.options.filter((option) => start.draggedIds.has(option.id));
            const clamped = clampOverlayGroupDelta(dragged.map((option) => option.position), dx, dy);
            const nextOptions = start.options.map((option) => start.draggedIds.has(option.id)
                ? {
                    ...option,
                    placed: true,
                    position: {
                        ...option.position,
                        xPct: option.position.xPct + clamped.dx,
                        yPct: option.position.yPct + clamped.dy,
                    },
                }
                : option);
            onUpdate({ ...element, config: { ...element.config, ec__options: nextOptions } });
        };
        const onMouseUp = () => {
            dragStartRef.current = null;
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };
    const applyPageOptions = (mutate) => {
        const draggedIds = new Set(pageOptions.map((option) => option.id));
        const nextOptions = (element.config.ec__options ?? []).map((option) => draggedIds.has(option.id) ? mutate(option) : option);
        onUpdate({ ...element, config: { ...element.config, ec__options: nextOptions } });
    };
    const handleKeyDown = (e) => {
        const result = readOverlayKey(e.key, e, anchor.position, { widthPct: 0.01, heightPct: 0.01 });
        if (!result) {
            return;
        }
        if (result.kind === 'select') {
            e.preventDefault();
            onSelect(element.id);
            return;
        }
        if (result.kind === 'delete') {
            e.preventDefault();
            onAnnounce(translate('{label} removed', { label: accessibleName }));
            onDelete(element.id);
            return;
        }
        if (result.kind === 'resize') {
            e.preventDefault();
            onAnnounce(translate('Response options cannot be resized'));
            return;
        }
        e.preventDefault();
        e.stopPropagation();
        if (result.kind === 'page') {
            const page = nextOverlayPage(anchor.page, result.delta, totalPages);
            if (page === null) {
                onAnnounce(result.delta < 0 ? translate('Already on the first page') : translate('Already on the last page'));
                return;
            }
            applyPageOptions((option) => ({ ...option, page, placed: true }));
            onPageChange(page);
            onAnnounce(translate('Moved to page {page}', { page }));
            return;
        }
        const rawDx = result.position.xPct - anchor.position.xPct;
        const rawDy = result.position.yPct - anchor.position.yPct;
        const { dx, dy } = clampOverlayGroupDelta(pageOptions.map((option) => option.position), rawDx, rawDy);
        if (dx === 0 && dy === 0) {
            onAnnounce(translate('Cannot move further'));
            return;
        }
        applyPageOptions((option) => ({
            ...option,
            placed: true,
            position: {
                ...option.position,
                xPct: option.position.xPct + dx,
                yPct: option.position.yPct + dy,
            },
        }));
        onAnnounce(translate('Moved to {x} percent from the left, {y} percent from the top', {
            x: overlayPercent(anchor.position.xPct + dx),
            y: overlayPercent(anchor.position.yPct + dy),
        }));
    };
    return (_jsxs("div", { ref: containerRef, role: 'group', tabIndex: isTabStop ? 0 : -1, "aria-label": accessibleName, "data-overlay-item": `hdr-${element.id}-p${pageNumber ?? anchor.page}`, "data-testid": `consent-choice-group-header-${element.id}-p${pageNumber ?? anchor.page}`, style: {
            position: 'absolute',
            left: `${anchor.position.xPct * 100}%`,
            top: `calc(${anchor.position.yPct * 100}% - ${HEADER_HEIGHT_PX + 4}px)`,
            height: HEADER_HEIGHT_PX,
            backgroundColor: HEADER_FILL,
            border: `1px solid ${HEADER_BORDER}`,
            color: HEADER_TEXT,
            outline: isSelected ? `2px solid ${HEADER_SELECTED_OUTLINE}` : 'none',
            outlineOffset: isSelected ? -2 : undefined,
            cursor: 'grab',
            userSelect: 'none',
        }, className: 'flex items-center gap-1 whitespace-nowrap rounded px-1.5 shadow-sm', onMouseDown: handleMouseDown, onFocus: () => onSelect(element.id), onKeyDown: handleKeyDown, title: translate('Drag to move this group\u2019s options on this page'), children: [_jsx(Icon, { custom: TypeIcon, size: 13, color: 'blue' }), _jsx("span", { className: 'truncate text-[11px] font-semibold leading-none', children: def.label }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'configure', "data-testid": `configure-choice-group-${element.id}`, className: 'rounded p-0.5 leading-none hover:bg-black hover:bg-opacity-5', "aria-label": translate('Configure'), onClick: (e) => {
                    e.stopPropagation();
                    onSelect(element.id);
                }, title: translate('Configure'), children: _jsx(Icon, { name: 'cog', size: 12, color: 'blue' }) }), _jsx("button", { type: 'button', tabIndex: -1, "data-action": 'delete', "data-testid": `delete-choice-group-${element.id}`, className: 'rounded p-0.5 leading-none hover:bg-black hover:bg-opacity-5', "aria-label": translate('Remove all options'), onClick: (e) => {
                    e.stopPropagation();
                    onDelete(element.id);
                }, title: translate('Remove all options'), children: _jsx(Icon, { name: 'close', size: 12, color: 'blue' }) })] }));
};
